import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { rolesMap, redirectIfNotGranted } from '../../services/auth';
import GlobalLayout from '../GlobalLayout';
import { getUsers, deleteUser } from '../../services/admin_api';

const normalizeCollection = (response) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    return [];
};

const getUserFullName = (user) => {
    const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim();
    return fullName || 'Utilisateur sans nom';
};

const getUserInitials = (user) => {
    const initials = [user?.firstname, user?.lastname]
        .filter(Boolean)
        .map((part) => part.trim().charAt(0).toUpperCase())
        .join('');

    return initials || (user?.email ? user.email.charAt(0).toUpperCase() : 'U');
};

const getClientLabel = (user) => user?.client_name || user?.client?.client_name || user?.client?.name || '';

const normalizeRoleLabel = (role) => {
    if (role === null || role === undefined) {
        return '';
    }

    if (typeof role === 'string') {
        return role.trim();
    }

    if (typeof role === 'object') {
        return role.label || role.name || role.code || (role.id ? String(role.id) : '');
    }

    return String(role).trim();
};

const normalizeUserRoles = (roles) => {
    if (Array.isArray(roles)) {
        return roles.map(normalizeRoleLabel).filter(Boolean);
    }

    if (typeof roles === 'string') {
        const trimmedRoles = roles.trim();

        if (!trimmedRoles) {
            return [];
        }

        try {
            return normalizeUserRoles(JSON.parse(trimmedRoles));
        } catch {
            if (trimmedRoles.includes(',')) {
                return trimmedRoles.split(',').map((role) => role.trim()).filter(Boolean);
            }

            return [trimmedRoles];
        }
    }

    if (roles && typeof roles === 'object') {
        return [normalizeRoleLabel(roles)].filter(Boolean);
    }

    return [];
};

const sortTextValues = (values) => [...values].sort((firstValue, secondValue) => firstValue.localeCompare(secondValue, 'fr', { sensitivity: 'base' }));

const sortUsers = (users) => [...users].sort((firstUser, secondUser) => {
    const lastNameComparison = (firstUser?.lastname || '').localeCompare(secondUser?.lastname || '', 'fr', { sensitivity: 'base' });

    if (lastNameComparison !== 0) {
        return lastNameComparison;
    }

    const firstNameComparison = (firstUser?.firstname || '').localeCompare(secondUser?.firstname || '', 'fr', { sensitivity: 'base' });

    if (firstNameComparison !== 0) {
        return firstNameComparison;
    }

    return (firstUser?.email || '').localeCompare(secondUser?.email || '', 'fr', { sensitivity: 'base' });
});

const UserList = () => {
    redirectIfNotGranted(rolesMap.admin);
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchUsers = async () => {
        setLoading(true);

        try {
            const data = await getUsers(1, 1000);
            setUsers(normalizeCollection(data));
            setError('');
        } catch (fetchError) {
            console.error('Failed to fetch users:', fetchError);
            setUsers([]);
            setError('Erreur lors de la récupération des utilisateurs.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        setPage(0);
    }, [searchTerm, selectedClient, selectedRole]);

    const sortedUsers = sortUsers(users);
    const clientOptions = sortTextValues([...new Set(sortedUsers.map((user) => getClientLabel(user)).filter(Boolean))]);
    const roleOptions = sortTextValues([...new Set(sortedUsers.flatMap((user) => normalizeUserRoles(user.roles)).filter(Boolean))]);
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const filteredUsers = sortedUsers.filter((user) => {
        const clientLabel = getClientLabel(user);
        const roles = normalizeUserRoles(user.roles);
        const searchTarget = [
            getUserFullName(user),
            user?.email || '',
            clientLabel,
            ...roles
        ].join(' ').toLowerCase();

        if (normalizedSearchTerm && !searchTarget.includes(normalizedSearchTerm)) {
            return false;
        }

        if (selectedClient && clientLabel !== selectedClient) {
            return false;
        }

        if (selectedRole && !roles.includes(selectedRole)) {
            return false;
        }

        return true;
    });

    const maxPage = Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1);
    const safePage = Math.min(page, maxPage);
    const paginatedUsers = filteredUsers.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
    const activeFiltersCount = [searchTerm.trim(), selectedClient, selectedRole].filter(Boolean).length;

    useEffect(() => {
        if (page !== safePage) {
            setPage(safePage);
        }
    }, [page, safePage]);

    const handleEdit = (user) => {
        navigate(`/user/edit/${user.id}`);
    };

    const handleDelete = async (user) => {
        const userLabel = getUserFullName(user);

        if (!window.confirm(`Supprimer l'utilisateur ${userLabel} ?`)) {
            return;
        }

        setDeletingId(user.id);

        try {
            await deleteUser(user.id);
            setUsers((previousUsers) => previousUsers.filter((currentUser) => currentUser.id !== user.id));
            setError('');
        } catch (deleteError) {
            console.error('Failed to delete user:', deleteError);
            setError('Impossible de supprimer cet utilisateur.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setSelectedClient('');
        setSelectedRole('');
    };

    return (
        <GlobalLayout>
            <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1480, mx: 'auto' }}>
                <Paper
                    elevation={0}
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        p: { xs: 2.5, md: 3 },
                        mb: 3,
                        borderRadius: 4,
                        color: '#12344d',
                        background: 'linear-gradient(135deg, #f8fbff 0%, #eef6ff 52%, #f4fbf6 100%)',
                        border: '1px solid #d9e7f0'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            right: -40,
                            bottom: -50,
                            width: 180,
                            height: 180,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(59,130,246,0.20) 0%, rgba(59,130,246,0) 72%)'
                        }}
                    />

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                        sx={{ position: 'relative' }}
                    >
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                Liste des utilisateurs
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#486581', maxWidth: 760 }}>
                                Filtrez rapidement les comptes par nom, client ou rôle, puis accédez aux actions d’administration sans parcourir toute la table.
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={fetchUsers}
                                disabled={loading}
                                sx={{ borderRadius: 2, minWidth: 140 }}
                            >
                                Actualiser
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/user/create')}
                                sx={{ borderRadius: 2, px: 2.5, boxShadow: 'none' }}
                            >
                                Nouvel utilisateur
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 3 }}>
                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Utilisateurs
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {users.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            comptes chargés
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Résultats
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {filteredUsers.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            après filtrage
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Clients
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {clientOptions.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            clients représentés
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Rôles
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {roleOptions.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            rôles détectés
                        </Typography>
                    </Paper>
                </Stack>

                <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                    <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems={{ xs: 'stretch', xl: 'center' }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Recherche"
                            placeholder="Nom, email, client ou rôle"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel id="user-client-filter-label">Client</InputLabel>
                            <Select
                                labelId="user-client-filter-label"
                                value={selectedClient}
                                label="Client"
                                onChange={(event) => setSelectedClient(event.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Tous les clients</em>
                                </MenuItem>
                                {clientOptions.map((clientLabel) => (
                                    <MenuItem key={clientLabel} value={clientLabel}>
                                        {clientLabel}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel id="user-role-filter-label">Rôle</InputLabel>
                            <Select
                                labelId="user-role-filter-label"
                                value={selectedRole}
                                label="Rôle"
                                onChange={(event) => setSelectedRole(event.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Tous les rôles</em>
                                </MenuItem>
                                {roleOptions.map((roleLabel) => (
                                    <MenuItem key={roleLabel} value={roleLabel}>
                                        {roleLabel}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            onClick={resetFilters}
                            disabled={!searchTerm.trim() && !selectedClient && !selectedRole}
                            sx={{ minWidth: { xl: 160 }, height: 40 }}
                        >
                            Réinitialiser
                        </Button>
                    </Stack>

                    {activeFiltersCount > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                            {searchTerm.trim() && (
                                <Chip
                                    label={`Recherche: ${searchTerm.trim()}`}
                                    onDelete={() => setSearchTerm('')}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {selectedClient && (
                                <Chip
                                    label={`Client: ${selectedClient}`}
                                    onDelete={() => setSelectedClient('')}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {selectedRole && (
                                <Chip
                                    label={`Rôle: ${selectedRole}`}
                                    onDelete={() => setSelectedRole('')}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    )}
                </Paper>

                {loading && (
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography>Chargement des utilisateurs...</Typography>
                    </Paper>
                )}

                {!loading && error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {!loading && (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                    <TableCell sx={{ fontWeight: 700, width: 90 }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Utilisateur</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 220 }}>Client</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Rôles</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 130 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedUsers.length > 0 ? (
                                    paginatedUsers.map((user) => {
                                        const userRoles = normalizeUserRoles(user.roles);
                                        const clientLabel = getClientLabel(user);

                                        return (
                                            <TableRow key={user.id} hover>
                                                <TableCell sx={{ color: '#52606d', whiteSpace: 'nowrap' }}>
                                                    #{user.id}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Avatar sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }}>
                                                            {getUserInitials(user)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 700, color: '#102a43' }}>
                                                                {getUserFullName(user)}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                                                                {user.email || 'Email non renseigné'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    {clientLabel ? (
                                                        <Chip
                                                            label={clientLabel}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ borderColor: '#d9e2ec', backgroundColor: '#f8fafc', color: '#334e68' }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: '#9aa5b1' }}>
                                                            Aucun client
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {userRoles.length > 0 ? (
                                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                                            {userRoles.map((role) => (
                                                                <Chip
                                                                    key={`${user.id}-${role}`}
                                                                    label={role}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ borderColor: '#d9e2ec', color: '#334e68' }}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: '#9aa5b1' }}>
                                                            Aucun rôle
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Tooltip title="Éditer">
                                                            <IconButton
                                                                color="primary"
                                                                onClick={() => handleEdit(user)}
                                                                aria-label="edit"
                                                                sx={{ border: '1px solid #dbe2ea', borderRadius: 2 }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Supprimer">
                                                            <span>
                                                                <IconButton
                                                                    color="error"
                                                                    onClick={() => handleDelete(user)}
                                                                    aria-label="delete"
                                                                    disabled={deletingId === user.id}
                                                                    sx={{ border: '1px solid #fee2e2', borderRadius: 2 }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ py: 5, textAlign: 'center' }}>
                                            <Typography sx={{ fontWeight: 600, color: '#102a43', mb: 0.5 }}>
                                                Aucun utilisateur trouvé
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                                {activeFiltersCount > 0
                                                    ? 'Ajustez les filtres ou réinitialisez la recherche pour élargir le résultat.'
                                                    : 'Aucun utilisateur n’est disponible pour le moment.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            component="div"
                            count={filteredUsers.length}
                            rowsPerPage={rowsPerPage}
                            page={safePage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Lignes"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                        />
                    </TableContainer>
                )}
            </Box>
        </GlobalLayout>
    );
};

export default UserList;
